package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px


// ---------- Hero ----------

val HomeHeroTitleStyle = CssStyle.base {
    Modifier.fontFamily(*RdFont.DISPLAY).css(
        "font-size" to "clamp(2.5rem, 1.3rem + 4.6vw, 4.6rem)", "font-weight" to 900,
        "font-stretch" to "118%", "line-height" to .98, "letter-spacing" to "-.02em"
    )
}

val HomePortalNoteStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.FlexStart)
            .gap(12.px)
            .backgroundColor(RdColor.SkySoft)
            .border(1.px, LineStyle.Solid, RdColor.LogoBlue)
            .borderRadius(12.px)
            .color(RdColor.Muted)
            .textDecorationLine(TextDecorationLine.None)
            .css("max-width" to "560px", "padding" to "12px 16px", "font-size" to ".93rem", "line-height" to 1.45, "transition" to "border-color .15s")
    }
    hover { Modifier.css("border-color" to "rgb(46,75,110)") }
    cssRule(" svg") { Modifier.color(RdColor.Brand).css("flex" to "none", "margin-top" to "1px") }
    cssRule(" b") { Modifier.color(RdColor.Ink).css("font-weight" to 600) }
}

val HomePortalNoteLinkStyle = CssStyle.base {
    Modifier
        .color(RdColor.Brand)
        .textDecorationLine(TextDecorationLine.Underline)
        .css("font-weight" to 600, "text-underline-offset" to "3px", "white-space" to "nowrap")
}

val HomeMosaicStyle = CssStyle.base {
    grid("1fr 1fr").css("gap" to "clamp(18px, 2.4vw, 28px)", "padding-bottom" to "36px", "min-width" to "0")
}

val HomeMosaicTileStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .borderRadius(16.px)
            .css("margin" to "0", "overflow" to "hidden", "aspect-ratio" to "4 / 3.4", "max-width" to "100%", "box-shadow" to RdSize.SHADOW)
    }
    cssRule(":nth-child(even)") { Modifier.css("transform" to "translateY(36px)") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "100%", "object-fit" to "cover") }
}

val HomeMosaicCaptionStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .backgroundColor(RdColor.Paper)
        .color(RdColor.Ink)
        .borderRadius(6.px)
        .css(
            "left" to "10px", "bottom" to "10px", "padding" to "7px 9px",
            "font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase"
        )
}

// ---------- Featured designs ----------

val HomeDesignsGridStyle = CssStyle {
    base { grid("1fr").gap(20.px) }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1.35fr 1fr", "grid-template-rows" to "auto auto") }
}

val HomeDesignCardStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Ink)
            .position(Position.Relative)
            .display(DisplayStyle.Grid)
            .backgroundColor(RdColor.Paper)
            .border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(16.px)
            .textDecorationLine(TextDecorationLine.None)
            .css("grid-template-rows" to "1fr auto", "overflow" to "hidden", "min-width" to "0")
    }
    cssRule(" figure") { Modifier.position(Position.Relative).css("margin" to "0", "overflow" to "hidden", "min-height" to "220px") }
    cssRule(" img") {
        Modifier.position(Position.Absolute).display(DisplayStyle.Block)
            .css("inset" to "0", "width" to "100%", "height" to "100%", "object-fit" to "cover", "transition" to "transform .5s ease")
    }
    cssRule(":hover img") { Modifier.css("transform" to "scale(1.04)") }
    cssRule(":hover .home-design-go") { Modifier.css("transform" to "translateX(3px)") }
}

/** The large featured card spans both rows on wider screens. */
val HomeDesignCardLeadStyle = CssStyle {
    Breakpoint.MD { Modifier.css("grid-row" to "span 2") }
}

/** Taller image for the featured card. */
val HomeDesignLeadFigureStyle = CssStyle {
    base { Modifier.css("min-height" to "260px !important") }
    Breakpoint.MD { Modifier.css("min-height" to "360px !important") }
}

val HomeDesignBodyStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween).alignItems(AlignItems.End)
            .gap(16.px).css("padding" to "20px 22px")
    }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(6.px).css("min-width" to "0") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
}

val HomeDesignGoStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Grid)
        .backgroundColor(RdColor.LogoBlue)
        .color(RdColor.Ink)
        .borderRadius(50.px)
        .css("flex" to "none", "width" to "44px", "height" to "44px", "place-items" to "center", "font-family" to RdFont.MONO_STACK, "transition" to "transform .2s")
}

val HomeDesignBadgeStyle = CssStyle.base {
    Modifier.position(Position.Absolute).css("top" to "14px", "left" to "14px", "z-index" to 1)
}

val HomeDesignsFootStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
        .color(RdColor.Muted).css("margin-top" to "22px", "flex-wrap" to "wrap", "gap" to "12px 24px")
}

// ---------- Trades ----------

val HomeTradesGridStyle = CssStyle {
    base { grid("1fr 1fr").gap(12.px) }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "repeat(4, 1fr)") }
}

val HomeTradeLinkStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Ink)
            .display(DisplayStyle.Flex)
            .justifyContent(JustifyContent.SpaceBetween)
            .alignItems(AlignItems.Center)
            .gap(10.px)
            .backgroundColor(RdColor.Paper)
            .border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(12.px)
            .textDecorationLine(TextDecorationLine.None)
            .css("padding" to "18px", "font-weight" to 600, "transition" to "border-color .15s, transform .15s")
    }
    hover { Modifier.css("border-color" to "rgb(30,40,52)", "transform" to "translateY(-2px)") }
    cssRule(" span") { Modifier.color(RdColor.Muted).css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}") }
}

val HomeTradeAllStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Ink).color(RdColor.Ground).css("border-color" to "rgb(30,40,52)") }
    cssRule(" span") { Modifier.color(RdColor.LogoBlue) }
}

// ---------- Portal band ----------

val HomeBandStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Band).color(RdColor.BandText) }
    cssRule(" .rd-eyebrow") { Modifier.color(RdColor.BandMuted) }
    cssRule(" .rd-lede") { Modifier.color(RdColor.BandMuted) }
}

val HomePortalGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(32px, 5vw, 64px)") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val HomeStackStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(22.px).css("min-width" to "0")
}

val HomeTicksStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px).css("list-style" to "none", "padding" to "0") }
    cssRule(" li") { Modifier.display(DisplayStyle.Grid).gap(10.px).css("grid-template-columns" to "22px 1fr") }
    cssRule(" li::before") { Modifier.color(RdColor.LogoBlue).css("content" to "\"✓\"", "font-weight" to 700) }
}

val HomeOfferStyle = CssStyle {
    base { Modifier.css("display" to "inline-flex").alignItems(AlignItems.Center).gap(12.px).color(RdColor.BandMuted).css("font-size" to ".95rem") }
    cssRule(" b") {
        Modifier.backgroundColor(RdColor.LogoBlue).color(RdColor.Ink).borderRadius(6.px)
            // One line, so on phones the badge doesn't wrap into a tall box; the text next to it wraps instead.
            .css("font" to "800 .95rem/1 ${RdFont.DISPLAY_STACK}", "font-stretch" to "110%", "padding" to "8px 10px",
                "flex" to "none", "white-space" to "nowrap", "text-align" to "center")
    }
}

/**
 * The portal shown on a desktop screen. The PNG is transparent, so it sits straight on the dark band; the box only
 * trims the empty space above and below the screen, and the picture is never enlarged beyond its own size.
 */
val HomePortalImageStyle = CssStyle {
    base { Modifier.css("aspect-ratio" to "1 / 0.835", "max-width" to "100%", "overflow" to "hidden") }
    cssRule(" img") {
        Modifier.display(DisplayStyle.Block)
            .css("width" to "100%", "height" to "100%", "object-fit" to "cover", "object-position" to "50% 50%")
    }
}

// ---------- Why work with me ----------

val HomeWhyGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "36px 40px") }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(8.px).css("padding-top" to "18px", "border-top" to "1px solid rgb(220,227,236)") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".97rem") }
}

// ---------- Contact ----------

val HomeContactCardStyle = CssStyle {
    base {
        grid("1fr").backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(20.px)
            .css("gap" to "clamp(32px, 5vw, 64px)", "padding" to "clamp(24px, 4vw, 48px)")
    }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val HomeContactCopyStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(18.px).css("align-content" to "start", "min-width" to "0")
}

val HomeContactDirectStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px).css("margin-top" to "8px") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Flex).gap(12.px).css("align-items" to "center", "justify-content" to "space-between", "max-width" to "420px") }
    cssRule(" a") { Modifier.display(DisplayStyle.Grid).gap(2.px).color(RdColor.Ink).textDecorationLine(TextDecorationLine.None).css("min-width" to "0") }
    cssRule(" a span") {
        Modifier.color(RdColor.Muted).css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
    }
    cssRule(" b") { Modifier.css("font-weight" to 600, "overflow-wrap" to "anywhere") }
    cssRule(" a:hover b") { Modifier.color(RdColor.Brand).textDecorationLine(TextDecorationLine.Underline) }
}

/** Restyles the shared ContactForm, which sets inline styles on its fields (hence !important). */
val HomeContactFormStyle = CssStyle {
    base { Modifier.position(Position.Relative).display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).css("min-width" to "0") }
    cssRule(" .form-label") { Modifier.color(RdColor.Ink).css("font" to "600 .88rem/1.3 ${RdFont.BODY_STACK}") }
    cssRule(" .form-control") {
        Modifier.color(RdColor.Ink).css(
            "font" to "400 1rem/1.4 ${RdFont.BODY_STACK} !important", "background" to "rgb(245,247,250) !important",
            "border" to "1px solid rgb(220,227,236) !important", "border-radius" to "10px !important", "padding" to "12px 14px !important"
        )
    }
    cssRule(" .form-control:focus") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "border-color" to "transparent !important") }
    cssRule(" button") {
        Modifier.css(
            "width" to "auto !important", "min-width" to "170px", "height" to "auto !important", "padding" to "15px 22px",
            "border-radius" to "10px !important", "font" to "600 1rem/1 ${RdFont.BODY_STACK} !important", "box-shadow" to "none !important"
        )
    }
    cssRule(" button:hover") { Modifier.css("background" to "rgb(34,54,79) !important") }
}

/** Shown in place of the form once a message is sent: a tick, a thank-you and a way to send another. */
val HomeContactSuccessStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.SkySoft).border(1.px, LineStyle.Solid, RdColor.LogoBlue)
            .borderRadius(16.px).css(
                "width" to "100%", "align-self" to "stretch", "align-content" to "center", "justify-items" to "center",
                "gap" to "14px", "text-align" to "center", "padding" to "clamp(28px, 5vw, 48px) clamp(20px, 4vw, 40px)"
            )
    }
    cssRule(" h3") { Modifier.css("outline" to "none", "max-width" to "24ch") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("max-width" to "40ch") }
    cssRule(" button") {
        Modifier.backgroundColor(RdColor.Brand).color(RdColor.BrandInk).css("margin-top" to "6px", "border" to "0", "cursor" to "pointer")
    }
    cssRule(" button:focus-visible") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "outline-offset" to "3px") }
}

/** The tick in a brand-blue circle. */
val HomeContactSuccessIconStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Brand).color(RdColor.BrandInk)
        .css("width" to "56px", "height" to "56px", "border-radius" to "50%", "place-items" to "center", "box-shadow" to "0 0 0 8px rgba(46,75,110,.12)")
}

/** The success panel fades and rises in (skipped for people who ask for less motion). */
val HomeContactSuccessIn = Keyframes {
    from { Modifier.css("opacity" to "0", "transform" to "translateY(8px)") }
    to { Modifier.css("opacity" to "1", "transform" to "none") }
}
